import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Package, Clock, Tag, User, DollarSign, FileText, Link2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { theme } from '../theme';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreatorUtilityDetailPage() {
  const { utilityId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [attachCount, setAttachCount] = useState(0);
  const [attachedPortfolios, setAttachedPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/creator/utilities/${utilityId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      // New canonical response shape: { utility, attach_count, attached_portfolios }
      setListing(res.data.utility || res.data.listing);
      setAttachCount(res.data.attach_count || 0);
      setAttachedPortfolios(res.data.attached_portfolios || []);
    }).catch(() => toast.error('Utility not found'))
      .finally(() => setLoading(false));
  }, [utilityId]);

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading...</div>;
  if (!listing) return <div style={{ padding: '60px', textAlign: 'center', color: theme.colors.text.secondary }}>Not found</div>;

  const statusColor = listing.status === 'ACTIVE' ? theme.colors.status.success : theme.colors.status.warning;

  return (
    <div data-testid="utility-detail-page" style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px' }}>
      <button data-testid="utility-detail-back" onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: '14px', fontWeight: 600, marginBottom: '24px' }}>
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <h1 data-testid="utility-detail-title" style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, margin: 0 }}>{listing.title}</h1>
          <span data-testid="utility-detail-status" style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, background: `${statusColor}18`, color: statusColor }}>{listing.status}</span>
        </div>
        <p style={{ fontSize: '15px', color: theme.colors.text.secondary, lineHeight: 1.6 }}>{listing.description}</p>
      </div>

      {/* Info Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <Card><CardContent className="pt-4 pb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Tag size={14} color={theme.colors.primary} /><span style={{ fontSize: '12px', color: theme.colors.text.muted }}>Type</span>
          </div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>{listing.fulfillment_type || 'DIGITAL'}</p>
        </CardContent></Card>

        <Card><CardContent className="pt-4 pb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <DollarSign size={14} color={theme.colors.status.success} /><span style={{ fontSize: '12px', color: theme.colors.text.muted }}>Pricing</span>
          </div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>
            {listing.pricing_model === 'FREE' ? 'Free' : `$${listing.price_amount || 0} ${listing.currency || 'USD'}`}
          </p>
        </CardContent></Card>

        <Card><CardContent className="pt-4 pb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Clock size={14} color={theme.colors.status.warning} /><span style={{ fontSize: '12px', color: theme.colors.text.muted }}>Duration</span>
          </div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>{listing.duration_days ? `${listing.duration_days} days` : 'Unlimited'}</p>
        </CardContent></Card>

        <Card><CardContent className="pt-4 pb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Package size={14} color={theme.colors.primary} /><span style={{ fontSize: '12px', color: theme.colors.text.muted }}>Max Redemptions</span>
          </div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>{listing.max_redemptions || 'Unlimited'}</p>
        </CardContent></Card>
      </div>

      {/* Provider */}
      <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <User size={16} color={theme.colors.text.muted} />
          <div>
            <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Provider</p>
            <p data-testid="utility-detail-provider" style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>{listing.provider_name}</p>
          </div>
        </div>
      </CardContent></Card>

      {/* Redeem Instructions */}
      {listing.redeem_instructions && (
        <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FileText size={14} color={theme.colors.primary} />
            <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Redeem Instructions</p>
          </div>
          <p style={{ fontSize: '14px', color: theme.colors.text.secondary, lineHeight: 1.6 }}>{listing.redeem_instructions}</p>
        </CardContent></Card>
      )}

      {/* Requirements */}
      {listing.requirements && (
        <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <CheckCircle size={14} color={theme.colors.status.success} />
            <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Requirements</p>
          </div>
          <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>{listing.requirements}</p>
        </CardContent></Card>
      )}

      {/* Attached Portfolios */}
      <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Link2 size={14} color={theme.colors.primary} />
          <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Attached to {attachCount} Portfolio{attachCount !== 1 ? 's' : ''}</p>
        </div>
        {attachedPortfolios.length === 0 ? (
          <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>Not attached to any portfolios yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {attachedPortfolios.map(p => (
              <div key={p.id} onClick={() => navigate(`/creator/portfolios/${p.id}`)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '8px', background: theme.colors.bg.secondary, cursor: 'pointer' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: theme.colors.text.primary }}>{p.name}</span>
                <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>{p.status}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      {/* Categories */}
      {listing.category_tags?.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {listing.category_tags.map(tag => (
            <span key={tag} style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', background: `${theme.colors.primary}14`, color: theme.colors.primary, fontWeight: 500 }}>{tag}</span>
          ))}
        </div>
      )}

      <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Created: {new Date(listing.created_at).toLocaleDateString()}</p>
    </div>
  );
}
